import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Save, Mail, Shield, AlertCircle, CheckCircle, Loader2, Send, Sparkles, Database, Download, RotateCcw, Clock, Scale, Award, Key, Eye, EyeOff, BookOpen, Plus, Edit2, Trash2, X, Star } from 'lucide-react';
import { getUser as getAuthUser, authenticatedFetch } from '@/lib/auth';
import { useConfirm, useToast } from './ui';
import {
  getSystemSettings,
  updateSMTPSettings,
  updateValidationRules,
  updateAIPrompts,
  updateLegalText,
} from '@/lib/admin';
import type { User } from '@/types';
import ESCOSettings from './settings/ESCOSettings';
import QualificationFrameworkSettings from './settings/QualificationFrameworkSettings';

interface SMTPSettings {
  host: string;
  port: number;
  username: string;
  password: string;
  from_email: string;
  from_name: string;
  openai_api_key?: string;
  serper_api_key?: string;
}

interface ValidationRulesConfig {
  min_knowledge_items: number;
  min_skills_items: number;
  min_competence_items: number;
  min_ects_hours: number;
  max_ects_hours: number;
  min_validation_score: number;
  // New threshold fields
  auto_approve_threshold: number;
  manual_review_threshold: number;
  minimum_hours: number;
}

interface AIPrompts {
  validation_prompt_id: string | null;
  validation_prompt: string;
  generation_prompt_id: string | null;
  generation_prompt: string;
  exam_prompt_id: string | null;
  exam_prompt: string;
  literature_suggest_prompt_id: string | null;
  literature_suggest_prompt: string;
  improvement_prompt_id: string | null;
  improvement_prompt: string;
}

interface CuratedSource {
  id: string;
  title: string;
  url: string;
  literature_type: 'pdf' | 'word' | 'webpage' | 'report' | 'video' | 'other';
  description: string;
  keywords: string[];
  categories: string[];
  quality_level: number;
  language: string;
  publisher?: string;
  publication_year?: number;
  verified_date: string;
  verified_by: string;
  last_checked: string;
  url_status: 'active' | 'dead_link' | 'moved' | 'archived';
  active: boolean;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export default function SystemSettings() {
  const { confirm } = useConfirm();
  const toast = useToast();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [smtpSettings, setSmtpSettings] = useState<SMTPSettings>({
    host: '',
    port: 587,
    username: '',
    password: '',
    from_email: '',
    from_name: 'Kompetanseforbundet',
  });

  const [validationRules, setValidationRules] = useState<ValidationRulesConfig>({
    min_knowledge_items: 3,
    min_skills_items: 3,
    min_competence_items: 2,
    min_ects_hours: 25,
    max_ects_hours: 30,
    min_validation_score: 70,
    // New threshold fields with recommended defaults
    auto_approve_threshold: 80,
    manual_review_threshold: 60,
    minimum_hours: 4,
  });

  const [aiPrompts, setAiPrompts] = useState<AIPrompts>({
    validation_prompt_id: null,
    validation_prompt: '',
    generation_prompt_id: null,
    generation_prompt: '',
    exam_prompt_id: null,
    exam_prompt: '',
    literature_suggest_prompt_id: null,
    literature_suggest_prompt: '',
    improvement_prompt_id: null,
    improvement_prompt: '',
  });

  const [testEmail, setTestEmail] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);

  // Backup/Restore state
  const [backups, setBackups] = useState<any[]>([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [restoringBackup, setRestoringBackup] = useState<string | null>(null);
  const [backupDescription, setBackupDescription] = useState('');
  const [showRestoreModal, setShowRestoreModal] = useState<any>(null);
  const [currentEnvironment, setCurrentEnvironment] = useState<string>('');

  // AI Prompts sub-tab state
  const [aiPromptsSubTab, setAiPromptsSubTab] = useState<'validation' | 'generation' | 'improvement' | 'exam' | 'literature' | 'frameworks'>('validation');

  // Curated Literature Sources state
  const [curatedSources, setCuratedSources] = useState<CuratedSource[]>([]);
  const [loadingCuratedSources, setLoadingCuratedSources] = useState(false);
  const [showAddSourceModal, setShowAddSourceModal] = useState(false);
  const [editingSource, setEditingSource] = useState<CuratedSource | null>(null);
  const [deletingSourceId, setDeletingSourceId] = useState<string | null>(null);

  // Determine active tab from URL
  const getActiveTabFromPath = (): 'email' | 'generation' | 'exam' | 'openai' | 'backup' | 'legal' | 'ai-prompts' | 'esco' => {
    const path = location.pathname;
    if (path.includes('/ai-prompts')) return 'ai-prompts'; // Show all AI prompts together
    if (path.includes('/legal')) return 'legal';
    if (path.includes('/backup')) return 'backup';
    if (path.includes('/esco')) return 'esco';
    return 'email'; // default
  };

  const activeTab = getActiveTabFromPath();

  const [legalText, setLegalText] = useState({
    id: null as string | null,
    content: ''
  });

  useEffect(() => {
    loadSettings();
    loadBackups();
  }, []);

  // Auto-scroll to top when success message is shown
  useEffect(() => {
    if (success) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [success]);

  // Load curated sources when on literature tab
  useEffect(() => {
    if (activeTab === 'ai-prompts' && aiPromptsSubTab === 'literature') {
      loadCuratedSources();
    }
  }, [activeTab, aiPromptsSubTab]);

  const loadSettings = async () => {
    try {
      // Use custom auth instead of Supabase auth
      const userProfile = getAuthUser();
      if (!userProfile) throw new Error('Not authenticated');

      // Check if user is systemadmin
      if (userProfile.role !== 'systemadmin') {
        throw new Error('Kun systemadministratorer har tilgang til denne siden');
      }

      setCurrentUser(userProfile);

      // Load all settings from API
      const settings = await getSystemSettings(userProfile.id);

      // Set SMTP settings
      if (settings.smtp) {
        setSmtpSettings({
          host: settings.smtp.host || '',
          port: settings.smtp.port || 587,
          username: settings.smtp.username || '',
          password: '', // Never load password for security
          from_email: settings.smtp.from_email || '',
          from_name: settings.smtp.from_name || 'Kompetanseforbundet',
          openai_api_key: settings.smtp.openai_api_key || '',
          serper_api_key: settings.smtp.serper_api_key || '',
        });
      }

      // Set validation rules
      if (settings.validation_rules) {
        setValidationRules({
          min_knowledge_items: settings.validation_rules.min_knowledge_items,
          min_skills_items: settings.validation_rules.min_skills_items,
          min_competence_items: settings.validation_rules.min_competence_items,
          min_ects_hours: settings.validation_rules.min_ects_hours,
          max_ects_hours: settings.validation_rules.max_ects_hours,
          min_validation_score: settings.validation_rules.min_validation_score,
          // New threshold fields (cast to any to handle optional fields from API)
          auto_approve_threshold: (settings.validation_rules as any).auto_approve_threshold ?? 80,
          manual_review_threshold: (settings.validation_rules as any).manual_review_threshold ?? 60,
          minimum_hours: (settings.validation_rules as any).minimum_hours ?? 4,
        });
      }

      // Set AI prompts
      setAiPrompts({
        validation_prompt_id: settings.ai_prompts.validation?.id || null,
        validation_prompt: settings.ai_prompts.validation?.content || '',
        generation_prompt_id: settings.ai_prompts.generation?.id || null,
        generation_prompt: settings.ai_prompts.generation?.content || '',
        exam_prompt_id: settings.ai_prompts.exam?.id || null,
        exam_prompt: settings.ai_prompts.exam?.content || '',
        literature_suggest_prompt_id: settings.ai_prompts.literature_suggest?.id || null,
        literature_suggest_prompt: settings.ai_prompts.literature_suggest?.content || '',
        improvement_prompt_id: settings.ai_prompts.improvement?.id || null,
        improvement_prompt: settings.ai_prompts.improvement?.content || '',
      });

      // Set legal text
      setLegalText({
        id: settings.legal_text?.id || null,
        content: settings.legal_text?.content || ''
      });

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSMTP = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const user = getAuthUser();
      if (!user) throw new Error('Not authenticated');

      await updateSMTPSettings(user.id, {
        host: smtpSettings.host,
        port: smtpSettings.port,
        username: smtpSettings.username,
        password: smtpSettings.password || undefined,
        from_email: smtpSettings.from_email,
        from_name: smtpSettings.from_name,
      });

      setSuccess('SMTP-innstillinger lagret');
      // Clear password field
      setSmtpSettings({ ...smtpSettings, password: '' });
    } catch (err: any) {
      setError('Feil ved lagring: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveValidationRules = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const user = getAuthUser();
      if (!user) throw new Error('Not authenticated');

      await updateValidationRules(user.id, validationRules);

      setSuccess('Valideringsregler lagret');
    } catch (err: any) {
      setError('Feil ved lagring: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAIPrompts = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const user = getAuthUser();
      if (!user) throw new Error('Not authenticated');

      await updateAIPrompts(user.id, {
        validation_prompt_id: aiPrompts.validation_prompt_id || undefined,
        validation_prompt: aiPrompts.validation_prompt,
        generation_prompt_id: aiPrompts.generation_prompt_id || undefined,
        generation_prompt: aiPrompts.generation_prompt,
      });

      setSuccess('AI-prompts lagret. Endringene vil tre i kraft ved neste validering/generering.');
    } catch (err: any) {
      setError('Feil ved lagring av AI-prompts: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGenerationPrompt = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const user = getAuthUser();
      if (!user) throw new Error('Not authenticated');

      await updateAIPrompts(user.id, {
        generation_prompt_id: aiPrompts.generation_prompt_id || undefined,
        generation_prompt: aiPrompts.generation_prompt,
      });

      setSuccess('KI-genereringsprompt lagret. Endringene vil tre i kraft ved neste studieplan-generering.');
    } catch (err: any) {
      setError('Feil ved lagring av KI-genereringsprompt: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveExamPrompt = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const user = getAuthUser();
      if (!user) throw new Error('Not authenticated');

      await updateAIPrompts(user.id, {
        exam_prompt_id: aiPrompts.exam_prompt_id || undefined,
        exam_prompt: aiPrompts.exam_prompt,
      });

      setSuccess('KI-prøve prompt lagret. Endringene vil tre i kraft ved neste prøvegenerering.');
    } catch (err: any) {
      setError('Feil ved lagring av KI-prøve prompt: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveImprovementPrompt = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const user = getAuthUser();
      if (!user) throw new Error('Not authenticated');

      await updateAIPrompts(user.id, {
        improvement_prompt_id: aiPrompts.improvement_prompt_id || undefined,
        improvement_prompt: aiPrompts.improvement_prompt,
      });

      setSuccess('KI-forslag prompt lagret. Endringene vil tre i kraft ved neste KI-forslag.');
    } catch (err: any) {
      setError('Feil ved lagring av KI-forslag prompt: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLiteratureSuggestPrompt = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const user = getAuthUser();
      if (!user) throw new Error('Not authenticated');

      await updateAIPrompts(user.id, {
        literature_suggest_prompt_id: aiPrompts.literature_suggest_prompt_id || undefined,
        literature_suggest_prompt: aiPrompts.literature_suggest_prompt,
      });

      setSuccess('KI-litteraturforslag prompt lagret. Endringene vil tre i kraft ved neste litteraturforslag.');
    } catch (err: any) {
      setError('Feil ved lagring av KI-litteraturforslag prompt: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Curated Sources Functions
  const loadCuratedSources = async () => {
    setLoadingCuratedSources(true);
    try {
      const response = await authenticatedFetch('/api/admin/curated-sources');
      const data = await response.json();

      if (data.success) {
        setCuratedSources(data.sources || []);
      } else {
        setError('Kunne ikke laste kurerte kilder: ' + data.error);
      }
    } catch (err: any) {
      setError('Feil ved lasting av kurerte kilder: ' + err.message);
    } finally {
      setLoadingCuratedSources(false);
    }
  };

  const handleCreateOrUpdateSource = async (sourceData: Partial<CuratedSource>) => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const endpoint = editingSource
        ? `/api/admin/curated-sources/${editingSource.id}`
        : '/api/admin/curated-sources';

      const method = editingSource ? 'PUT' : 'POST';

      const response = await authenticatedFetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sourceData)
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(editingSource ? 'Kilde oppdatert!' : 'Ny kilde lagt til!');
        setShowAddSourceModal(false);
        setEditingSource(null);
        await loadCuratedSources();
      } else {
        setError(data.error || 'Kunne ikke lagre kilde');
      }
    } catch (err: any) {
      setError('Feil ved lagring av kilde: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSource = async (sourceId: string) => {
    const confirmed = await confirm({
      title: 'Slett kilde',
      message: 'Er du sikker på at du vil slette denne kilden? Dette kan ikke angres.',
      confirmLabel: 'Slett',
      cancelLabel: 'Avbryt',
      variant: 'danger'
    });

    if (!confirmed) return;

    setDeletingSourceId(sourceId);
    setError('');
    setSuccess('');

    try {
      const response = await authenticatedFetch(`/api/admin/curated-sources/${sourceId}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Kilde slettet!');
        await loadCuratedSources();
      } else {
        setError(data.error || 'Kunne ikke slette kilde');
      }
    } catch (err: any) {
      setError('Feil ved sletting av kilde: ' + err.message);
    } finally {
      setDeletingSourceId(null);
    }
  };

  const handleToggleSourceActive = async (source: CuratedSource) => {
    setSaving(true);
    try {
      const response = await authenticatedFetch(`/api/admin/curated-sources/${source.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...source, active: !source.active })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(`Kilde ${source.active ? 'deaktivert' : 'aktivert'}!`);
        await loadCuratedSources();
      } else {
        setError(data.error || 'Kunne ikke oppdatere kilde');
      }
    } catch (err: any) {
      setError('Feil ved oppdatering av kilde: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLegalText = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const user = getAuthUser();
      if (!user) throw new Error('Not authenticated');

      await updateLegalText(user.id, legalText.id, legalText.content);

      setSuccess('Lovverk-tekst lagret. Dette vises nå på offentlige studieplaner.');
      await loadSettings(); // Reload to get the new ID if it was an insert
    } catch (err: any) {
      setError('Feil ved lagring av lovverk-tekst: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail.trim()) {
      toast.showWarning('Vennligst oppgi en e-postadresse');
      return;
    }

    setTestingEmail(true);
    setError('');
    setSuccess('');

    try {
      const response = await authenticatedFetch('/api/test', {
        method: 'POST',
        body: JSON.stringify({
          to: testEmail,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send test email');
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to send test email');
      }

      setSuccess('Test-e-post sendt til ' + testEmail);
    } catch (err: any) {
      setError('Feil ved sending av test-e-post: ' + err.message);
    } finally {
      setTestingEmail(false);
    }
  };

  const loadBackups = async () => {
    setLoadingBackups(true);
    try {
      const user = getAuthUser();
      if (!user) return;

      const response = await authenticatedFetch(`/api/admin/backups?user_id=${user.id}`);

      if (!response.ok) throw new Error('Failed to load backups');

      const data = await response.json();
      setBackups(data.backups || []);
      setCurrentEnvironment(data.environment || 'unknown');
    } catch (err: any) {
      console.error('Load backups error:', err);
      setError('Feil ved lasting av sikkerhetskopier: ' + err.message);
    } finally {
      setLoadingBackups(false);
    }
  };

  const handleCreateBackup = async () => {
    if (!backupDescription.trim()) {
      setError('Vennligst oppgi en beskrivelse for sikkerhetskopien');
      return;
    }

    setCreatingBackup(true);
    setError('');
    setSuccess('');

    try {
      const user = getAuthUser();
      if (!user) throw new Error('Not authenticated');

      const response = await authenticatedFetch('/api/admin/backups/create', {
        method: 'POST',
        body: JSON.stringify({
          user_id: user.id,
          description: backupDescription
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create backup');
      }

      const data = await response.json();
      setSuccess('Sikkerhetskopi opprettet: ' + data.backup.backup_id);
      setBackupDescription('');

      // Reload backups list
      await loadBackups();
    } catch (err: any) {
      setError('Feil ved opprettelse av sikkerhetskopi: ' + err.message);
    } finally {
      setCreatingBackup(false);
    }
  };

  const handleRestoreBackup = async (backupId: string) => {
    setRestoringBackup(backupId);
    setError('');
    setSuccess('');

    try {
      const user = getAuthUser();
      if (!user) throw new Error('Not authenticated');

      const response = await authenticatedFetch(`/api/admin/backups/restore/${backupId}`, {
        method: 'POST',
        body: JSON.stringify({
          user_id: user.id,
          confirmation: 'YES'
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to restore backup');
      }

      setSuccess('Gjenoppretting startet - systemet vil starte på nytt om noen sekunder...');
      setShowRestoreModal(null);

      // Reload page after 5 seconds
      setTimeout(() => {
        window.location.reload();
      }, 5000);
    } catch (err: any) {
      setError('Feil ved gjenoppretting: ' + err.message);
      setRestoringBackup(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="animate-spin" size={48} />
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'systemadmin') {
    return (
      <div className="card text-center py-12">
        <Shield className="mx-auto mb-4 text-red-600" size={64} />
        <h2 className="text-xl font-bold mb-2">Ingen tilgang</h2>
        <p className="text-gray-600">
          Kun systemadministratorer har tilgang til systeminnstillinger.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Systeminnstillinger</h1>
        <p className="text-gray-600 mt-2">
          Konfigurer system-omfattende innstillinger og regler
        </p>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-6 flex items-center gap-2">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded mb-6 flex items-center gap-2">
          <CheckCircle size={20} />
          {success}
        </div>
      )}

      {/* Tabs Navigation - Now handled by Settings wrapper */}
      {/* <div className="mb-6 border-b border-gray-200">
        <div className="flex flex-wrap gap-2 -mb-px">
          <button
            onClick={() => setActiveTab('email')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'email'
                ? 'border-kf-primary text-kf-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Mail size={18} />
              <span className="hidden sm:inline">E-post innstillinger</span>
              <span className="sm:hidden">E-post</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('validation')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'validation'
                ? 'border-kf-primary text-kf-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Shield size={18} />
              <span className="hidden sm:inline">KI Validering</span>
              <span className="sm:hidden">Validering</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('generation')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'generation'
                ? 'border-kf-primary text-kf-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Sparkles size={18} />
              <span className="hidden sm:inline">KI Generering</span>
              <span className="sm:hidden">Generering</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('exam')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'exam'
                ? 'border-kf-primary text-kf-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Award size={18} />
              <span className="hidden sm:inline">KI-prøve</span>
              <span className="sm:hidden">Prøve</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('openai')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'openai'
                ? 'border-kf-primary text-kf-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Key size={18} />
              <span className="hidden sm:inline">API-nøkler</span>
              <span className="sm:hidden">API</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('legal')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'legal'
                ? 'border-kf-primary text-kf-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Scale size={18} />
              <span className="hidden sm:inline">Lovverk</span>
              <span className="sm:hidden">Lovverk</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('backup')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'backup'
                ? 'border-kf-primary text-kf-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Database size={18} />
              <span className="hidden sm:inline">Sikkerhetskopier</span>
              <span className="sm:hidden">Backup</span>
            </div>
          </button>
        </div>
      </div> */}

      {/* Tab Content */}
      {activeTab === 'email' && (
        <>
      {/* SMTP Settings */}
      <div className="card mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Mail className="text-kf-primary" size={24} />
          <h2 className="text-xl font-semibold">SMTP-konfigurasjon</h2>
        </div>

        <p className="text-sm text-gray-600 mb-6">
          Konfigurer e-postserver for utsending av verifiseringskoder og varsler.
        </p>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">SMTP Server (Host) *</label>
              <input
                type="text"
                className="input"
                placeholder="smtp.proisp.no"
                value={smtpSettings.host}
                onChange={(e) => setSmtpSettings({ ...smtpSettings, host: e.target.value })}
              />
            </div>

            <div>
              <label className="label">Port *</label>
              <input
                type="number"
                className="input"
                placeholder="587"
                value={smtpSettings.port}
                onChange={(e) => setSmtpSettings({ ...smtpSettings, port: parseInt(e.target.value) })}
              />
            </div>
          </div>

          <div>
            <label className="label">Brukernavn *</label>
            <input
              type="text"
              className="input"
              placeholder="din@epost.no"
              value={smtpSettings.username}
              onChange={(e) => setSmtpSettings({ ...smtpSettings, username: e.target.value })}
            />
          </div>

          <div>
            <label className="label">Passord</label>
            <input
              type="password"
              className="input"
              placeholder="La stå tom for å beholde eksisterende passord"
              value={smtpSettings.password}
              onChange={(e) => setSmtpSettings({ ...smtpSettings, password: e.target.value })}
            />
            <p className="text-xs text-gray-500 mt-1">
              Av sikkerhetsgrunner vises ikke eksisterende passord. La feltet stå tomt for å beholde.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Avsender e-post *</label>
              <input
                type="email"
                className="input"
                placeholder="noreply@kompetanseforbundet.no"
                value={smtpSettings.from_email}
                onChange={(e) => setSmtpSettings({ ...smtpSettings, from_email: e.target.value })}
              />
            </div>

            <div>
              <label className="label">Avsendernavn *</label>
              <input
                type="text"
                className="input"
                placeholder="Kompetanseforbundet"
                value={smtpSettings.from_name}
                onChange={(e) => setSmtpSettings({ ...smtpSettings, from_name: e.target.value })}
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={handleSaveSMTP}
              disabled={saving}
              className="btn btn-primary flex items-center gap-2"
            >
              {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
              Lagre SMTP-innstillinger
            </button>
          </div>
        </div>
      </div>

      {/* Test Email */}
      <div className="card mb-6 bg-kf-light">
        <h3 className="text-lg font-semibold mb-4">Test e-postutsending</h3>
        <p className="text-sm text-gray-700 mb-4">
          Send en test-e-post for å verifisere at SMTP-konfigurasjonen fungerer.
        </p>
        <div className="flex gap-3">
          <input
            type="email"
            className="input flex-1"
            placeholder="test@example.com"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
          />
          <button
            onClick={handleTestEmail}
            disabled={testingEmail}
            className="btn btn-secondary flex items-center gap-2"
          >
            {testingEmail ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
            Send test-e-post
          </button>
        </div>
      </div>
        </>
      )}

      {/* Validation Tab */}

      {/* Generation Tab */}
      {activeTab === 'generation' && (
        <>
      {/* AI Generation Prompt */}
      <div className="card mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Sparkles className="text-kf-accent" size={24} />
          <h2 className="text-xl font-semibold">KI-genereringsprompt</h2>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Denne prompten sendes til KI-agenten når nye studieplaner skal genereres fra brukerens beskrivelse. Her defineres krav til innhold, struktur, kompetansepoeng (i tråd med reglene for ECTS), læringsutbytte og moduler.
        </p>

        <div className="space-y-6">
          <div>
            <textarea
              className="input font-mono text-sm"
              rows={15}
              value={aiPrompts.generation_prompt}
              onChange={(e) => setAiPrompts({ ...aiPrompts, generation_prompt: e.target.value })}
              placeholder="Skriv AI-genereringsprompt her..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Fokuser på faglige krav, ELMv3/EU-standarder og pedagogiske prinsipper. Unngå å definere JSON-struktur.
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={handleSaveAIPrompts}
              disabled={saving}
              className="btn btn-primary flex items-center gap-2"
            >
              {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
              Lagre genereringsprompt
            </button>
          </div>

          <div className="bg-kf-accent-20 border border-kf-accent-40 rounded p-4 mt-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="text-kf-warning flex-shrink-0 mt-0.5" size={18} />
              <div className="text-sm text-kf-accent">
                <p className="font-medium mb-1">Viktig!</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Endringer i KI-prompts påvirker all fremtidig validering og generering.</li>
                  <li>Test grundig etter endringer for å sikre at resultater er som forventet.</li>
                  <li>Både admin (studieforbund) og brukere bruker samme prompts.</li>
                  <li>Minimum valideringsscore (%-terskel) settes i "Valideringsregler" over.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
        </>
      )}

      {/* Exam Tab */}
      {activeTab === 'exam' && (
        <>
      {/* AI Exam Prompt */}
      <div className="card mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Award className="text-kf-accent" size={24} />
          <h2 className="text-xl font-semibold">KI-prøve prompt</h2>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Denne prompten sendes til KI-agenten når prøvespørsmål skal genereres basert på en studieplan. Her defineres krav til spørsmålstyper, vanskelighetsgrad, dekning av læringsutbytte og vurderingskriterier.
        </p>

        <div className="space-y-6">
          <div>
            <textarea
              className="input font-mono text-sm"
              rows={20}
              value={aiPrompts.exam_prompt}
              onChange={(e) => setAiPrompts({ ...aiPrompts, exam_prompt: e.target.value })}
              placeholder="Skriv KI-prøve prompt her..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Fokuser på pedagogiske prinsipper, spørsmålstyper (flervalg, sant/usant, skriftlig), og hvordan spørsmål skal dekke læringsutbyttene. Bruk {"{{placeholder}}"} for dynamiske verdier (f.eks. {"{{title}}"}, {"{{knowledge}}"}, {"{{modules}}"}).
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={handleSaveExamPrompt}
              disabled={saving}
              className="btn btn-primary flex items-center gap-2"
            >
              {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
              Lagre prøve-prompt
            </button>
          </div>

          <div className="bg-kf-accent-20 border border-kf-accent-40 rounded p-4 mt-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="text-kf-warning flex-shrink-0 mt-0.5" size={18} />
              <div className="text-sm text-kf-accent">
                <p className="font-medium mb-1">Viktig!</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Endringer i KI-prompts påvirker all fremtidig generering av prøvespørsmål.</li>
                  <li>Test grundig etter endringer for å sikre at resultater er som forventet.</li>
                  <li>Prompten må returnere gyldig JSON med foreslått prøveinfo og spørsmål.</li>
                  <li>Bruk {"{{placeholder}}"} for dynamiske verdier fra studieplanen.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
        </>
      )}

      {/* AI Prompts Tab - Shows all AI prompts together */}
      {activeTab === 'ai-prompts' && (
        <>
      {/* AI Prompts Sub-Tab Navigation */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex flex-wrap gap-2 -mb-px">
          <button
            onClick={() => setAiPromptsSubTab('validation')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              aiPromptsSubTab === 'validation'
                ? 'border-kf-primary text-kf-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Shield size={18} />
              <span>Validering</span>
            </div>
          </button>
          <button
            onClick={() => setAiPromptsSubTab('generation')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              aiPromptsSubTab === 'generation'
                ? 'border-kf-primary text-kf-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Sparkles size={18} />
              <span>Generering</span>
            </div>
          </button>
          <button
            onClick={() => setAiPromptsSubTab('improvement')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              aiPromptsSubTab === 'improvement'
                ? 'border-kf-primary text-kf-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <CheckCircle size={18} />
              <span>KI-forslag</span>
            </div>
          </button>
          <button
            onClick={() => setAiPromptsSubTab('exam')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              aiPromptsSubTab === 'exam'
                ? 'border-kf-primary text-kf-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Award size={18} />
              <span>Prøve</span>
            </div>
          </button>
          <button
            onClick={() => setAiPromptsSubTab('literature')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              aiPromptsSubTab === 'literature'
                ? 'border-kf-primary text-kf-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <BookOpen size={18} />
              <span>Litteraturforslag</span>
            </div>
          </button>
          <button
            onClick={() => setAiPromptsSubTab('frameworks')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              aiPromptsSubTab === 'frameworks'
                ? 'border-kf-primary text-kf-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Scale size={18} />
              <span>Kvalifikasjonsrammeverk</span>
            </div>
          </button>
        </div>
      </div>

      {/* AI Validation Prompt */}
      {aiPromptsSubTab === 'validation' && (
      <>
      {/* Validation Rules */}
      <div className="card mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="text-kf-primary" size={24} />
          <h2 className="text-xl font-semibold">Valideringsregler</h2>
        </div>

        <p className="text-sm text-gray-600 mb-6">
          Sett minimumskrav for studieplaner som sendes inn.
        </p>

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="label">Min. kunnskaper *</label>
              <input
                type="number"
                className="input"
                min="1"
                value={validationRules.min_knowledge_items}
                onChange={(e) => setValidationRules({ ...validationRules, min_knowledge_items: parseInt(e.target.value) })}
              />
            </div>

            <div>
              <label className="label">Min. ferdigheter *</label>
              <input
                type="number"
                className="input"
                min="1"
                value={validationRules.min_skills_items}
                onChange={(e) => setValidationRules({ ...validationRules, min_skills_items: parseInt(e.target.value) })}
              />
            </div>

            <div>
              <label className="label">Min. kompetanse *</label>
              <input
                type="number"
                className="input"
                min="1"
                value={validationRules.min_competence_items}
                onChange={(e) => setValidationRules({ ...validationRules, min_competence_items: parseInt(e.target.value) })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Min. timer pr. Kompetansepoeng:</label>
              <input
                type="number"
                className="input"
                min="20"
                max="35"
                value={validationRules.min_ects_hours}
                onChange={(e) => setValidationRules({ ...validationRules, min_ects_hours: parseInt(e.target.value) })}
              />
              <p className="text-xs text-gray-500 mt-1">Standard: 25 timer</p>
            </div>

            <div>
              <label className="label">Maks. timer pr. Kompetansepoeng:</label>
              <input
                type="number"
                className="input"
                min="20"
                max="35"
                value={validationRules.max_ects_hours}
                onChange={(e) => setValidationRules({ ...validationRules, max_ects_hours: parseInt(e.target.value) })}
              />
              <p className="text-xs text-gray-500 mt-1">Standard: 30 timer</p>
            </div>
          </div>

          <div>
            <label className="label">Minimum KI-valideringsscore (%) *</label>
            <input
              type="number"
              className="input"
              min="0"
              max="100"
              value={validationRules.min_validation_score}
              onChange={(e) => setValidationRules({ ...validationRules, min_validation_score: parseInt(e.target.value) })}
            />
            <p className="text-xs text-gray-500 mt-1">
              Studieplaner under denne scoren får advarsel. Standard: 70%
            </p>
          </div>

          {/* New Threshold Settings */}
          <div className="border-t pt-4 mt-4">
            <h3 className="font-medium mb-3">Terskelverdier for validering</h3>

            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
              <p className="text-sm text-blue-800">
                <strong>Anbefalte verdier:</strong> Selvgodkjenning ≥80%, Manuell vurdering 60-79%, Avvist &lt;60%, Minimum 4 timer
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="label">Terskel for selvgodkjenning (%)</label>
                <input
                  type="number"
                  className="input"
                  min="60"
                  max="100"
                  value={validationRules.auto_approve_threshold}
                  onChange={(e) => setValidationRules({ ...validationRules, auto_approve_threshold: parseInt(e.target.value) })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Score ≥ dette: kan selvgodkjennes
                </p>
              </div>

              <div>
                <label className="label">Terskel for manuell vurdering (%)</label>
                <input
                  type="number"
                  className="input"
                  min="0"
                  max={validationRules.auto_approve_threshold - 1}
                  value={validationRules.manual_review_threshold}
                  onChange={(e) => setValidationRules({ ...validationRules, manual_review_threshold: parseInt(e.target.value) })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Score ≥ dette: kan sendes med begrunnelse
                </p>
              </div>

              <div>
                <label className="label">Minimum timer</label>
                <input
                  type="number"
                  className="input"
                  min="1"
                  max="50"
                  value={validationRules.minimum_hours}
                  onChange={(e) => setValidationRules({ ...validationRules, minimum_hours: parseInt(e.target.value) })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum timer for Kompetanseattest
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={handleSaveValidationRules}
              disabled={saving}
              className="btn btn-primary flex items-center gap-2"
            >
              {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
              Lagre valideringsregler
            </button>
          </div>
        </div>
      </div>

      {/* KI Validation Prompt */}
      <div className="card mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="text-kf-accent" size={24} />
          <h2 className="text-xl font-semibold">KI-valideringsprompt</h2>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Denne prompten sendes til KI-agent når studieplaner valideres mot regelverket. I feltet under legges inn dette regelverket med valideringskriterier, kompetansepoeng(i tråd med ECTS), faglige krav (læringsutbytter, moduler, krav til innhold, struktur, m.v.)
        </p>

        <div className="space-y-6">
          <div>
            <textarea
              className="input font-mono text-sm"
              rows={15}
              value={aiPrompts.validation_prompt}
              onChange={(e) => setAiPrompts({ ...aiPrompts, validation_prompt: e.target.value })}
              placeholder="Skriv KI-valideringsprompt her..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Fokuser på scoringskriterier (0-100), faglige krav og norske språkkrav. Unngå å definere JSON-struktur.
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={handleSaveAIPrompts}
              disabled={saving}
              className="btn btn-primary flex items-center gap-2"
            >
              {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
              Lagre valideringsprompt
            </button>
          </div>

          <div className="bg-kf-accent-20 border border-kf-accent-40 rounded p-4 mt-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="text-kf-warning flex-shrink-0 mt-0.5" size={18} />
              <div className="text-sm text-kf-accent">
                <p className="font-medium mb-1">Viktig!</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Endringer i KI-prompts påvirker all fremtidig validering av studieplaner.</li>
                  <li>Test grundig etter endringer for å sikre at resultater er som forventet.</li>
                  <li>Prompten må returnere gyldig JSON med score og feedback.</li>
                  <li>Minimum valideringsscore (%-terskel) settes i feltene over.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      </>
      )}

      {/* AI Generation Prompt */}
      {aiPromptsSubTab === 'generation' && (
      <div className="card mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Sparkles className="text-kf-accent" size={24} />
          <h2 className="text-xl font-semibold">KI-genereringsprompt</h2>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Denne prompten sendes til KI-agenten når nye studieplaner skal genereres fra brukerens beskrivelse. Her defineres krav til innhold, struktur, kompetansepoeng (i tråd med reglene for ECTS), læringsutbytte og moduler.
        </p>

        <div className="space-y-6">
          <div>
            <textarea
              className="input font-mono text-sm"
              rows={15}
              value={aiPrompts.generation_prompt}
              onChange={(e) => setAiPrompts({ ...aiPrompts, generation_prompt: e.target.value })}
              placeholder="Skriv AI-genereringsprompt her..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Fokuser på faglige krav, ELMv3/EU-standarder og pedagogiske prinsipper. Unngå å definere JSON-struktur.
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={handleSaveGenerationPrompt}
              disabled={saving}
              className="btn btn-primary flex items-center gap-2"
            >
              {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
              Lagre genereringsprompt
            </button>
          </div>

          <div className="bg-kf-accent-20 border border-kf-accent-40 rounded p-4 mt-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="text-kf-warning flex-shrink-0 mt-0.5" size={18} />
              <div className="text-sm text-kf-accent">
                <p className="font-medium mb-1">Viktig!</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Endringer i KI-prompts påvirker all fremtidig generering av studieplaner.</li>
                  <li>Test grundig etter endringer for å sikre at resultater er som forventet.</li>
                  <li>Bruk {"{{placeholder}}"} for dynamiske verdier fra brukerens input.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* AI Improvement Prompt (KI-forslag) */}
      {aiPromptsSubTab === 'improvement' && (
      <div className="card mb-6">
        <div className="flex items-center gap-3 mb-4">
          <CheckCircle className="text-kf-accent" size={24} />
          <h2 className="text-xl font-semibold">KI-forslag prompt</h2>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Denne prompten sendes til KI-agenten når "Vis KI-forslag til justeringer" kjøres. Her defineres hvordan forbedringer skal foreslås basert på valideringsresultatet, inkludert Bloom-taksonomi analyse og EQF-nivå vurdering.
        </p>

        <div className="space-y-6">
          <div>
            <textarea
              className="input font-mono text-sm"
              rows={20}
              value={aiPrompts.improvement_prompt}
              onChange={(e) => setAiPrompts({ ...aiPrompts, improvement_prompt: e.target.value })}
              placeholder="Skriv KI-forslag prompt her..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Bruk {"{{placeholder}}"} for dynamiske verdier. Tilgjengelige: {"{{bloom_context}}"}, {"{{bloom_profile_analysis}}"}, {"{{qf_context}}"}, {"{{validation_feedback}}"}, {"{{title}}"}, {"{{description}}"}, etc.
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={handleSaveImprovementPrompt}
              disabled={saving}
              className="btn btn-primary flex items-center gap-2"
            >
              {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
              Lagre KI-forslag prompt
            </button>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded p-4 mt-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Synkronisering med validering</p>
                <p className="mb-2">Denne prompten bruker valideringsresultatet direkte for å foreslå forbedringer. Viktige plassholdere:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><code className="bg-blue-100 px-1 rounded">{"{{bloom_context}}"}</code> - Bloom-analyse med spesifikke læringsutbytter som har for lavt nivå</li>
                  <li><code className="bg-blue-100 px-1 rounded">{"{{bloom_profile_analysis}}"}</code> - Holistisk profilanalyse (overall_assessment, profile_balance, per_category_profile, recommendations)</li>
                  <li><code className="bg-blue-100 px-1 rounded">{"{{validation_feedback}}"}</code> - Komplett valideringsresultat som JSON</li>
                  <li><code className="bg-blue-100 px-1 rounded">{"{{expected_bloom_range}}"}</code> - Forventet Bloom-range for EQF-nivået</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-kf-accent-20 border border-kf-accent-40 rounded p-4 mt-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="text-kf-warning flex-shrink-0 mt-0.5" size={18} />
              <div className="text-sm text-kf-accent">
                <p className="font-medium mb-1">Viktig!</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Endringer påvirker alle fremtidige KI-forslag til justeringer.</li>
                  <li>Test grundig etter endringer for å sikre at forbedringer adresserer valideringsresultater.</li>
                  <li>Prompten må returnere gyldig JSON med improvements og summary.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* AI Exam Prompt */}
      {aiPromptsSubTab === 'exam' && (
      <div className="card mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Award className="text-kf-accent" size={24} />
          <h2 className="text-xl font-semibold">KI-prøve prompt</h2>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Denne prompten sendes til KI-agenten når prøvespørsmål skal genereres basert på en studieplan. Her defineres krav til spørsmålstyper, vanskelighetsgrad, og hvordan prøven skal struktureres.
        </p>

        <div className="space-y-6">
          <div>
            <textarea
              className="input font-mono text-sm"
              rows={20}
              value={aiPrompts.exam_prompt}
              onChange={(e) => setAiPrompts({ ...aiPrompts, exam_prompt: e.target.value })}
              placeholder="Skriv KI-prøve prompt her..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Fokuser på pedagogiske krav, spørsmålstyper (flervalg, åpne spørsmål, etc), og relevant faglig innhold. Bruk {"{{placeholder}}"} for dynamiske verdier (f.eks. {"{{title}}"}, {"{{knowledge}}"}).
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={handleSaveExamPrompt}
              disabled={saving}
              className="btn btn-primary flex items-center gap-2"
            >
              {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
              Lagre prøve-prompt
            </button>
          </div>

          <div className="bg-kf-accent-20 border border-kf-accent-40 rounded p-4 mt-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="text-kf-warning flex-shrink-0 mt-0.5" size={18} />
              <div className="text-sm text-kf-accent">
                <p className="font-medium mb-1">Viktig!</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Endringer i KI-prompts påvirker all fremtidig generering av prøvespørsmål.</li>
                  <li>Test grundig etter endringer for å sikre at resultater er som forventet.</li>
                  <li>Prompten må returnere gyldig JSON med prøveinfo og spørsmål.</li>
                  <li>Bruk {"{{placeholder}}"} for dynamiske verdier fra studieplanen.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* AI Literature Suggestion Prompt */}
      {aiPromptsSubTab === 'literature' && (
      <div className="card mb-6">
        <div className="flex items-center gap-3 mb-4">
          <BookOpen className="text-kf-accent" size={24} />
          <h2 className="text-xl font-semibold">KI-litteraturforslag prompt</h2>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Denne prompten sendes til KI-agenten når litteraturforslag skal genereres basert på en studieplan. Her defineres krav til reelle kilder, hvilke norske ressurser som skal prioriteres, og hvordan forslag skal returneres.
        </p>

        <div className="space-y-6">
          <div>
            <textarea
              className="input font-mono text-sm"
              rows={25}
              value={aiPrompts.literature_suggest_prompt}
              onChange={(e) => setAiPrompts({ ...aiPrompts, literature_suggest_prompt: e.target.value })}
              placeholder="Skriv KI-litteraturforslag prompt her..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Fokuser på å kreve REELLE kilder (regjeringen.no, ndla.no, etc), og unngå oppfunnede URL-er. Bruk {"{{placeholder}}"} for dynamiske verdier (f.eks. {"{{title}}"}, {"{{knowledge}}"}, {"{{count}}"}).
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={handleSaveLiteratureSuggestPrompt}
              disabled={saving}
              className="btn btn-primary flex items-center gap-2"
            >
              {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
              Lagre litteraturforslag-prompt
            </button>
          </div>

          <div className="bg-kf-accent-20 border border-kf-accent-40 rounded p-4 mt-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="text-kf-warning flex-shrink-0 mt-0.5" size={18} />
              <div className="text-sm text-kf-accent">
                <p className="font-medium mb-1">Viktig!</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Endringer i KI-prompts påvirker all fremtidig generering av litteraturforslag.</li>
                  <li>Test grundig etter endringer for å sikre at resultater er som forventet.</li>
                  <li>Prompten må returnere gyldig JSON med faktiske, eksisterende URL-er.</li>
                  <li>Bruk {"{{placeholder}}"} for dynamiske verdier fra studieplanen.</li>
                  <li>Vær svært streng på at AI-en kun foreslår reelle kilder som faktisk eksisterer.</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Curated Literature Sources Management */}
          <div className="mt-8 pt-8 border-t">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Star className="text-kf-accent" size={20} />
                  Kurerte litteraturkilder
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Administrer godkjente kilder som KI-en kan velge fra. Dette sikrer kvalitet og reduserer hallusinasjoner.
                </p>
              </div>
              <button
                onClick={() => {
                  setEditingSource(null);
                  setShowAddSourceModal(true);
                }}
                className="btn btn-primary flex items-center gap-2"
              >
                <Plus size={18} />
                Legg til kilde
              </button>
            </div>

            {loadingCuratedSources ? (
              <div className="text-center py-12">
                <Loader2 className="animate-spin mx-auto mb-2" size={32} />
                <p className="text-gray-600">Laster kilder...</p>
              </div>
            ) : curatedSources.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <BookOpen className="mx-auto mb-2 text-gray-400" size={48} />
                <p className="text-gray-600">Ingen kurerte kilder funnet.</p>
                <p className="text-sm text-gray-500 mt-1">Legg til kilder som KI-en kan velge fra.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tittel</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">URL</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategorier</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kvalitet</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bruk</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Handlinger</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {curatedSources.map((source) => (
                      <tr key={source.id} className={!source.active ? 'opacity-50' : ''}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            onClick={() => handleToggleSourceActive(source)}
                            className={`px-2 py-1 text-xs rounded-full font-medium ${
                              source.active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {source.active ? 'Aktiv' : 'Inaktiv'}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">{source.title}</div>
                          <div className="text-xs text-gray-500 mt-1">{source.description.substring(0, 80)}...</div>
                        </td>
                        <td className="px-4 py-3">
                          <a
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-kf-primary hover:underline flex items-center gap-1"
                          >
                            {source.url.substring(0, 40)}...
                          </a>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {source.literature_type}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {source.categories.slice(0, 2).map((cat, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full"
                              >
                                {cat}
                              </span>
                            ))}
                            {source.categories.length > 2 && (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                                +{source.categories.length - 2}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                size={14}
                                className={i < source.quality_level ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
                              />
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {source.usage_count}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => {
                                setEditingSource(source);
                                setShowAddSourceModal(true);
                              }}
                              className="text-kf-primary hover:text-kf-primary-dark"
                              title="Rediger"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteSource(source.id)}
                              disabled={deletingSourceId === source.id}
                              className="text-red-600 hover:text-red-800 disabled:opacity-50"
                              title="Slett"
                            >
                              {deletingSourceId === source.id ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : (
                                <Trash2 size={16} />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Add/Edit Source Modal */}
        {showAddSourceModal && (
          <SourceModal
            source={editingSource}
            onClose={() => {
              setShowAddSourceModal(false);
              setEditingSource(null);
            }}
            onSave={handleCreateOrUpdateSource}
            saving={saving}
          />
        )}
      </div>
      )}

      {/* Qualification Frameworks Sub-Tab */}
      {aiPromptsSubTab === 'frameworks' && (
        <QualificationFrameworkSettings />
      )}
        </>
      )}

      {/* API Keys Tab */}
      {activeTab === 'openai' && (
        <div className="space-y-6">
          {/* OpenAI API Key */}
          <div className="card">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="text-kf-accent" size={24} />
              <h2 className="text-xl font-semibold">OpenAI API-nøkkel</h2>
            </div>

            <p className="text-sm text-gray-600 mb-6">
              Brukes for KI-validering av studieplaner, generering av forbedringer og prøvespørsmål.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API-nøkkel
                </label>
                <div className="relative">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    className="input pr-10 font-mono text-sm"
                    value={smtpSettings.openai_api_key || ''}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, openai_api_key: e.target.value })}
                    placeholder="sk-proj-..."
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                    title={showApiKey ? 'Skjul nøkkel' : 'Vis nøkkel'}
                  >
                    {showApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Få din API-nøkkel fra{' '}
                  <a
                    href="https://platform.openai.com/account/api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-kf-primary hover:underline"
                  >
                    OpenAI Platform
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Serper API Key */}
          <div className="card">
            <div className="flex items-center gap-3 mb-4">
              <Key className="text-green-600" size={24} />
              <h2 className="text-xl font-semibold">Serper API-nøkkel (Web-søk)</h2>
            </div>

            <p className="text-sm text-gray-600 mb-6">
              Brukes for å finne alternative URLer til ødelagte litteraturlenker via Google-søk.
              Gratis for 2500 søk/måned.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API-nøkkel
                </label>
                <div className="relative">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    className="input pr-10 font-mono text-sm"
                    value={smtpSettings.serper_api_key || ''}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, serper_api_key: e.target.value })}
                    placeholder="abc123..."
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                    title={showApiKey ? 'Skjul nøkkel' : 'Vis nøkkel'}
                  >
                    {showApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Få din gratis API-nøkkel fra{' '}
                  <a
                    href="https://serper.dev"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-kf-primary hover:underline"
                  >
                    Serper.dev
                  </a>
                  {' '}(2500 gratis søk/måned)
                </p>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex gap-3">
            <button
              onClick={handleSaveSMTP}
              disabled={saving}
              className="btn btn-primary flex items-center gap-2"
            >
              {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
              Lagre API-nøkler
            </button>
          </div>

          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">Viktig informasjon</h3>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Nøklene lagres kryptert i databasen</li>
              <li>Kun systemadministratorer har tilgang til å endre nøklene</li>
              <li>OpenAI brukes for alle KI-funksjoner i systemet</li>
              <li>Serper brukes kun for URL-korreksjon av ødelagte litteraturlenker</li>
            </ul>
          </div>
        </div>
      )}

      {/* Legal Tab */}
      {activeTab === 'legal' && (
        <>
      {/* Legal Framework Text */}
      <div className="card mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Scale className="text-kf-accent" size={24} />
          <h2 className="text-xl font-semibold">Lovhjemmelsgrunnlag</h2>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Denne teksten vises på offentlige verifiseringssider for studieplaner og forklarer det juridiske rammeverket
          for kvalitetssikringen.
        </p>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-2">
            <Scale className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
            <div className="text-sm text-blue-900">
              <p className="font-medium mb-1">Vises på offentlige studieplaner</p>
              <p className="text-blue-700">
                Teksten du skriver her vil vises på verifiseringssiden når noen besøker en offentlig studieplan via QR-kode eller lenke.
                Den skal forklare lovhjemmelen for kvalitetssikringen og rammeverket for kompetanseattester/micro-credentials.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lovverk og standarder
            </label>
            <p className="text-xs text-gray-600 mb-2">
              Beskriv lovhjemmelsgrunnlaget (Voksenopplæringsloven, forskrifter, EU-regelverk, ELMv3, osv.)
            </p>
            <textarea
              className="input font-mono text-sm"
              rows={12}
              value={legalText.content}
              onChange={(e) => setLegalText({ ...legalText, content: e.target.value })}
              placeholder={`Eksempel:

Denne studieplanen er kvalitetssikret i henhold til:

- **Voksenopplæringsloven §10** - Krav til studieforbund
- **Forskrift for studieforbund** godkjent av Kunnskapsdepartementet
- **EU Micro-credentials Framework (2022)** - Europeisk rammeverk for kompetanseattester
- **ELMv3 (European Learning Model v3)** - Standardisering av læringsdata
- **EU-anbefalinger for læringsutbyttebeskrivelser**

Studieplanen danner grunnlag for kursleveranser som gir **Kompetanseattester** (norsk) / **Micro-credentials** (engelsk).`}
            />
            <p className="text-xs text-gray-500 mt-1">
              Bruk markdown-formatering for overskrifter, lister og lenker.
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={handleSaveLegalText}
              disabled={saving}
              className="btn btn-primary flex items-center gap-2"
            >
              {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
              Lagre lovverk-tekst
            </button>
          </div>
        </div>
      </div>
        </>
      )}

      {/* Backup Tab */}
      {activeTab === 'backup' && (
        <>
      {/* Backup & Restore */}
      <div className="card mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Database className="text-kf-primary" size={24} />
          <h2 className="text-xl font-semibold">Sikkerhetskopiering & Gjenoppretting</h2>
        </div>

        <p className="text-sm text-gray-600 mb-6">
          Opprett og administrer sikkerhetskopier av {currentEnvironment === 'test' ? 'test-miljøet' : 'produksjonsmiljøet'}.
          Du kan gjenopprette systemet til en tidligere tilstand.
        </p>

        {/* Create backup */}
        <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-6">
          <h3 className="font-semibold mb-3">Opprett ny sikkerhetskopi</h3>
          <div className="flex gap-3">
            <input
              type="text"
              className="input flex-1"
              placeholder="Beskrivelse (f.eks. 'Før oppdatering av validerings-regler')"
              value={backupDescription}
              onChange={(e) => setBackupDescription(e.target.value)}
              disabled={creatingBackup}
            />
            <button
              onClick={handleCreateBackup}
              disabled={creatingBackup || !backupDescription.trim()}
              className="btn btn-primary flex items-center gap-2"
            >
              {creatingBackup ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Oppretter...
                </>
              ) : (
                <>
                  <Download size={18} />
                  Opprett backup
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-gray-600 mt-2">
            Dette tar en fullstendig kopi av database, kildekode og konfigurasjon.
          </p>
        </div>

        {/* Backups list */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Tilgjengelige sikkerhetskopier ({backups.length}/10)</h3>
            <button
              onClick={loadBackups}
              disabled={loadingBackups}
              className="text-sm text-kf-primary hover:underline flex items-center gap-1"
            >
              {loadingBackups ? (
                <Loader2 className="animate-spin" size={14} />
              ) : (
                <RotateCcw size={14} />
              )}
              Oppdater
            </button>
          </div>

          {loadingBackups ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin" size={32} />
            </div>
          ) : backups.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Database size={48} className="mx-auto mb-3 text-gray-300" />
              <p>Ingen sikkerhetskopier funnet</p>
              <p className="text-sm">Opprett din første sikkerhetskopi over</p>
            </div>
          ) : (
            <div className="space-y-3">
              {backups.map((backup) => (
                <div
                  key={backup.backup_id}
                  className="border border-gray-200 rounded p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{backup.backup_id}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          backup.environment === 'test'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {backup.environment}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{backup.description}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {new Date(backup.date).toLocaleString('no-NO')}
                        </span>
                        <span>Størrelse: {backup.total_size}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowRestoreModal(backup)}
                      disabled={restoringBackup !== null}
                      className="btn btn-secondary text-sm flex items-center gap-2 ml-4"
                    >
                      <RotateCcw size={14} />
                      Gjenopprett
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-red-50 border border-red-200 rounded p-4 mt-6">
          <div className="flex items-start gap-2">
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
            <div className="text-sm text-red-800">
              <p className="font-medium mb-1">⚠️ Viktig informasjon om gjenoppretting</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Gjenoppretting er en destruktiv operasjon som overskriver alle nåværende data</li>
                <li>En automatisk sikkerhetskopi opprettes før gjenoppretting</li>
                <li>Systemet vil starte på nytt etter gjenoppretting</li>
                <li>Kun gjenopprett fra sikkerhetskopier i samme miljø (test/prod)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Restore confirmation modal */}
      {showRestoreModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4 text-red-600">Bekreft gjenoppretting</h3>
            <p className="mb-4">
              Er du sikker på at du vil gjenopprette systemet til denne sikkerhetskopien?
            </p>
            <div className="bg-gray-100 rounded p-3 mb-4">
              <p className="font-medium">{showRestoreModal.backup_id}</p>
              <p className="text-sm text-gray-600">{showRestoreModal.description}</p>
              <p className="text-xs text-gray-500 mt-1">
                {new Date(showRestoreModal.date).toLocaleString('no-NO')}
              </p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
              <p className="text-sm text-red-800 font-medium">
                ⚠️ Dette vil overskrive alle nåværende data!
              </p>
              <p className="text-xs text-red-700 mt-1">
                En sikkerhetskopi av nåværende tilstand opprettes automatisk før gjenoppretting.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRestoreModal(null)}
                disabled={restoringBackup !== null}
                className="flex-1 btn btn-secondary"
              >
                Avbryt
              </button>
              <button
                onClick={() => handleRestoreBackup(showRestoreModal.backup_id)}
                disabled={restoringBackup !== null}
                className="flex-1 btn bg-red-600 hover:bg-red-700 text-white flex items-center justify-center gap-2"
              >
                {restoringBackup ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Gjenoppretter...
                  </>
                ) : (
                  <>
                    <RotateCcw size={18} />
                    Ja, gjenopprett
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {/* ESCO Settings Tab */}
      {activeTab === 'esco' && (
        <div className="card">
          <ESCOSettings />
        </div>
      )}

      {/* System Info */}
      <div className="card bg-gray-50">
        <h3 className="text-lg font-semibold mb-4">Systeminformasjon</h3>
        <div className="space-y-2 text-sm text-gray-700">
          <div className="flex justify-between">
            <span>Innlogget som:</span>
            <span className="font-medium">{currentUser.full_name} ({currentUser.email})</span>
          </div>
          <div className="flex justify-between">
            <span>Rolle:</span>
            <span className="font-medium">Systemadministrator</span>
          </div>
          <div className="flex justify-between">
            <span>Versjon:</span>
            <span className="font-medium">1.0.0</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Source Modal Component
function SourceModal({
  source,
  onClose,
  onSave,
  saving
}: {
  source: CuratedSource | null;
  onClose: () => void;
  onSave: (data: Partial<CuratedSource>) => void;
  saving: boolean;
}) {
  const [formData, setFormData] = useState({
    title: source?.title || '',
    url: source?.url || '',
    literature_type: source?.literature_type || 'webpage' as const,
    description: source?.description || '',
    keywords: source?.keywords.join(', ') || '',
    categories: source?.categories.join(', ') || '',
    quality_level: source?.quality_level || 3,
    language: source?.language || 'no',
    publisher: source?.publisher || '',
    publication_year: source?.publication_year || undefined,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      keywords: formData.keywords.split(',').map(k => k.trim()).filter(Boolean),
      categories: formData.categories.split(',').map(c => c.trim()).filter(Boolean),
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white">
          <h3 className="text-xl font-semibold">
            {source ? 'Rediger kilde' : 'Legg til ny kilde'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tittel *
            </label>
            <input
              type="text"
              required
              className="input"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="F.eks. NDLA - Nasjonal digital læringsarena"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL *
            </label>
            <input
              type="url"
              required
              className="input"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              placeholder="https://www.eksempel.no/"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type *
            </label>
            <select
              required
              className="input"
              value={formData.literature_type}
              onChange={(e) => setFormData({ ...formData, literature_type: e.target.value as any })}
            >
              <option value="webpage">Nettside</option>
              <option value="pdf">PDF</option>
              <option value="word">Word-dokument</option>
              <option value="report">Rapport</option>
              <option value="video">Video</option>
              <option value="other">Annet</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Beskrivelse *
            </label>
            <textarea
              required
              className="input"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Kort beskrivelse av kilden..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nøkkelord (kommaseparert)
            </label>
            <input
              type="text"
              className="input"
              value={formData.keywords}
              onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
              placeholder="utdanning, læring, digital"
            />
            <p className="text-xs text-gray-500 mt-1">
              Brukes for å matche kilden mot studieplaner
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kategorier (kommaseparert) *
            </label>
            <input
              type="text"
              required
              className="input"
              value={formData.categories}
              onChange={(e) => setFormData({ ...formData, categories: e.target.value })}
              placeholder="Utdanning, Digitale ressurser"
            />
            <p className="text-xs text-gray-500 mt-1">
              F.eks: Utdanning, Økonomi, Samfunnskunnskap, Arbeidslivskompetanse
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kvalitetsnivå (1-5) *
              </label>
              <select
                required
                className="input"
                value={formData.quality_level}
                onChange={(e) => setFormData({ ...formData, quality_level: parseInt(e.target.value) })}
              >
                <option value="1">1 - Lav</option>
                <option value="2">2 - Under middels</option>
                <option value="3">3 - Middels</option>
                <option value="4">4 - God</option>
                <option value="5">5 - Utmerket</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Språk *
              </label>
              <select
                required
                className="input"
                value={formData.language}
                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
              >
                <option value="no">Norsk</option>
                <option value="en">Engelsk</option>
                <option value="sv">Svensk</option>
                <option value="da">Dansk</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Utgiver (valgfritt)
              </label>
              <input
                type="text"
                className="input"
                value={formData.publisher}
                onChange={(e) => setFormData({ ...formData, publisher: e.target.value })}
                placeholder="F.eks. Regjeringen"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Publiseringsår (valgfritt)
              </label>
              <input
                type="number"
                className="input"
                value={formData.publication_year || ''}
                onChange={(e) => setFormData({ ...formData, publication_year: e.target.value ? parseInt(e.target.value) : undefined })}
                placeholder="2024"
                min="1900"
                max={new Date().getFullYear()}
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary flex-1"
              disabled={saving}
            >
              Avbryt
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1 flex items-center justify-center gap-2"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Lagrer...
                </>
              ) : (
                <>
                  <Save size={18} />
                  {source ? 'Oppdater' : 'Legg til'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
